import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Clock, CheckCircle, Archive, Eye, Edit, Trash2, Sparkles, Loader2, Upload, ChevronDown, ChevronUp, Wand2, File, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminArticles = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAI, setShowAI] = useState(false);
  const [aiMode, setAiMode] = useState('generate'); // 'generate' or 'paste'
  const [aiForm, setAiForm] = useState({ topic: '', tone: 'professional', length: 'medium', audience: 'leaders and professionals working across cultures', include_cta: true, custom_instructions: '' });
  const [pasteText, setPasteText] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfForm, setPdfForm] = useState({ topic: '', tone: 'professional', length: 'medium', custom_instructions: '' });
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  const { token } = useAuth();

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const res = await fetch(`${API_URL}/api/cms/articles?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setArticles(data.articles); setTotal(data.total); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchArticles(); }, [page, statusFilter]);
  const handleSearch = (e) => { e.preventDefault(); fetchArticles(); };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await fetch(`${API_URL}/api/cms/articles/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchArticles();
  };

  const handleAIGenerate = async () => {
    setGenerating(true);
    setAiError('');
    try {
      const res = await fetch(`${API_URL}/api/ai/generate-article`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(aiForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Generation failed'); }
      const data = await res.json();
      // Create article as draft
      const createRes = await fetch(`${API_URL}/api/cms/articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data.article),
      });
      if (createRes.ok) {
        const created = await createRes.json();
        setShowAI(false);
        navigate(`/admin/articles/${created.id}/edit`);
      }
    } catch (e) { setAiError(e.message); }
    setGenerating(false);
  };

  const handlePasteFormat = async () => {
    if (!pasteText.trim()) return;
    setGenerating(true);
    setAiError('');
    try {
      const res = await fetch(`${API_URL}/api/ai/format-article`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ raw_text: pasteText }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Formatting failed'); }
      const data = await res.json();
      const createRes = await fetch(`${API_URL}/api/cms/articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data.article),
      });
      if (createRes.ok) {
        const created = await createRes.json();
        setShowAI(false);
        navigate(`/admin/articles/${created.id}/edit`);
      }
    } catch (e) { setAiError(e.message); }
    setGenerating(false);
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    setGenerating(true);
    setAiError('');
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('topic', pdfForm.topic);
      formData.append('tone', pdfForm.tone);
      formData.append('length', pdfForm.length);
      formData.append('custom_instructions', pdfForm.custom_instructions);

      const res = await fetch(`${API_URL}/api/ai/pdf-to-article`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'PDF processing failed'); }
      const data = await res.json();
      const createRes = await fetch(`${API_URL}/api/cms/articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data.article),
      });
      if (createRes.ok) {
        const created = await createRes.json();
        setShowAI(false);
        setPdfFile(null);
        navigate(`/admin/articles/${created.id}/edit`);
      }
    } catch (e) { setAiError(e.message); }
    setGenerating(false);
  };

  const statusColors = {
    draft: 'bg-cream/[0.05] text-cream/40',
    scheduled: 'bg-amber-500/10 text-amber-300',
    published: 'bg-green-500/10 text-green-400',
    archived: 'bg-cream/[0.03] text-cream/20',
  };
  const statusIcons = { draft: FileText, scheduled: Clock, published: CheckCircle, archived: Archive };
  const inputClass = "w-full bg-navy border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30";

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Articles</h1>
          <p className="text-cream/30 text-sm mt-1">{total} total articles</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAI(!showAI)} className="inline-flex items-center gap-1.5 px-3 py-2 border border-purple-500/30 text-purple-300 text-xs rounded-lg hover:border-purple-400/50 hover:bg-purple-500/10 transition-all" data-testid="ai-generate-btn">
            <Sparkles size={14} /> AI Generate {showAI ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          <Link to="/admin/articles/new" data-testid="new-article-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light transition-all">
            <Plus size={16} /> New Article
          </Link>
        </div>
      </div>

      {/* AI Generation Panel */}
      {showAI && (
        <div className="bg-navy border border-purple-500/20 rounded-xl p-6 mb-6" data-testid="ai-panel">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles size={18} className="text-purple-400" />
            <h2 className="text-cream text-sm font-semibold">AI Article Generator</h2>
          </div>

          {/* Mode tabs */}
          <div className="flex gap-1 mb-4 bg-navy-deep rounded-lg p-1 w-fit">
            <button onClick={() => setAiMode('generate')} className={`px-4 py-1.5 rounded text-xs font-medium transition-all ${aiMode === 'generate' ? 'bg-purple-500/20 text-purple-300' : 'text-cream/30 hover:text-cream/50'}`} data-testid="ai-mode-generate">
              <Wand2 size={12} className="inline mr-1" /> Generate from Topic
            </button>
            <button onClick={() => setAiMode('paste')} className={`px-4 py-1.5 rounded text-xs font-medium transition-all ${aiMode === 'paste' ? 'bg-purple-500/20 text-purple-300' : 'text-cream/30 hover:text-cream/50'}`} data-testid="ai-mode-paste">
              <Upload size={12} className="inline mr-1" /> Paste &amp; Format
            </button>
            <button onClick={() => setAiMode('pdf')} className={`px-4 py-1.5 rounded text-xs font-medium transition-all ${aiMode === 'pdf' ? 'bg-purple-500/20 text-purple-300' : 'text-cream/30 hover:text-cream/50'}`} data-testid="ai-mode-pdf">
              <File size={12} className="inline mr-1" /> PDF Upload
            </button>
          </div>

          {aiMode === 'generate' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Topic / Title *</label>
                <input value={aiForm.topic} onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })} placeholder="e.g. Why cultural fluency matters more than DEI training" className={inputClass} data-testid="ai-topic-input" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Tone</label>
                  <select value={aiForm.tone} onChange={(e) => setAiForm({ ...aiForm, tone: e.target.value })} className={inputClass} style={{colorScheme:'dark'}}>
                    <option value="professional">Professional</option>
                    <option value="conversational">Conversational</option>
                    <option value="academic">Academic</option>
                    <option value="inspirational">Inspirational</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Length</label>
                  <select value={aiForm.length} onChange={(e) => setAiForm({ ...aiForm, length: e.target.value })} className={inputClass} style={{colorScheme:'dark'}}>
                    <option value="short">Short (~500 words)</option>
                    <option value="medium">Medium (~1000 words)</option>
                    <option value="long">Long (~1500 words)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Audience</label>
                  <input value={aiForm.audience} onChange={(e) => setAiForm({ ...aiForm, audience: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Custom Instructions (optional)</label>
                <textarea value={aiForm.custom_instructions} onChange={(e) => setAiForm({ ...aiForm, custom_instructions: e.target.value })} placeholder="Any specific angle, references, or structure requirements..." rows={2} className={`${inputClass} resize-none`} />
              </div>
              <label className="flex items-center gap-2 text-cream/40 text-xs cursor-pointer">
                <input type="checkbox" checked={aiForm.include_cta} onChange={(e) => setAiForm({ ...aiForm, include_cta: e.target.checked })} className="rounded border-cream/20 bg-navy-deep text-purple-500 focus:ring-purple-500/30" />
                Include CFQ Assessment call-to-action
              </label>
            </div>
          ) : aiMode === 'paste' ? (
            <div>
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Paste Article Text</label>
              <p className="text-cream/15 text-[10px] mb-2">Paste text from Claude, Google Docs, Word, or any source. AI will structure it into article blocks.</p>
              <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder="Paste your article text here..." rows={10} className={`${inputClass} resize-none font-mono text-xs`} data-testid="ai-paste-input" />
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Upload PDF</label>
                <p className="text-cream/15 text-[10px] mb-2">Upload a PDF document. AI will extract text and generate a structured article from it.</p>
                <div className="relative">
                  {pdfFile ? (
                    <div className="flex items-center gap-3 bg-purple-500/10 border border-purple-500/20 rounded-lg px-4 py-3">
                      <File size={20} className="text-purple-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-cream text-sm font-medium truncate">{pdfFile.name}</p>
                        <p className="text-cream/30 text-[10px]">{(pdfFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button onClick={() => setPdfFile(null)} className="p-1 text-cream/30 hover:text-red-400"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-2 border-2 border-dashed border-purple-500/20 rounded-lg p-6 cursor-pointer hover:border-purple-500/40 hover:bg-purple-500/[0.03] transition-all" data-testid="pdf-drop-zone">
                      <File size={24} className="text-purple-400/50" />
                      <span className="text-cream/30 text-xs">Click to select PDF or drag and drop</span>
                      <span className="text-cream/15 text-[10px]">Max 20MB</span>
                      <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) setPdfFile(e.target.files[0]); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Topic / Angle (optional)</label>
                <input value={pdfForm.topic} onChange={(e) => setPdfForm({ ...pdfForm, topic: e.target.value })} placeholder="Focus on a specific aspect of the PDF..." className={inputClass} data-testid="pdf-topic-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Tone</label>
                  <select value={pdfForm.tone} onChange={(e) => setPdfForm({ ...pdfForm, tone: e.target.value })} className={inputClass} style={{colorScheme:'dark'}}>
                    <option value="professional">Professional</option>
                    <option value="conversational">Conversational</option>
                    <option value="academic">Academic</option>
                    <option value="inspirational">Inspirational</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Length</label>
                  <select value={pdfForm.length} onChange={(e) => setPdfForm({ ...pdfForm, length: e.target.value })} className={inputClass} style={{colorScheme:'dark'}}>
                    <option value="short">Short (~500 words)</option>
                    <option value="medium">Medium (~1000 words)</option>
                    <option value="long">Long (~1500 words)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Custom Instructions (optional)</label>
                <textarea value={pdfForm.custom_instructions} onChange={(e) => setPdfForm({ ...pdfForm, custom_instructions: e.target.value })} placeholder="Any specific angle, key quotes to include, structure preferences..." rows={2} className={`${inputClass} resize-none`} />
              </div>
            </div>
          )}

          {aiError && <p className="text-red-400 text-xs mt-2">{aiError}</p>}

          <div className="flex gap-2 mt-4">
            <button
              onClick={aiMode === 'generate' ? handleAIGenerate : aiMode === 'paste' ? handlePasteFormat : handlePdfUpload}
              disabled={generating || (aiMode === 'generate' ? !aiForm.topic.trim() : aiMode === 'paste' ? !pasteText.trim() : !pdfFile)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-500 disabled:opacity-40 transition-all"
              data-testid="ai-submit-btn"
            >
              {generating ? <><Loader2 size={14} className="animate-spin" /> {aiMode === 'pdf' ? 'Processing PDF...' : 'Generating...'}</> : <><Sparkles size={14} /> {aiMode === 'generate' ? 'Generate Article' : aiMode === 'paste' ? 'Format & Create' : 'Generate from PDF'}</>}
            </button>
            <button onClick={() => { setShowAI(false); setPdfFile(null); }} className="px-3 py-2 text-cream/30 text-xs hover:text-cream/50">Cancel</button>
          </div>
          <p className="text-cream/10 text-[9px] mt-3">Powered by Claude. Article will be created as a draft for review before publishing.</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-1 bg-navy border border-gold/[0.06] rounded-lg p-1">
          {['', 'draft', 'scheduled', 'published', 'archived'].map((s) => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${statusFilter === s ? 'bg-gold/[0.1] text-gold' : 'text-cream/30 hover:text-cream/50'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>
        <form onSubmit={handleSearch} className="flex-1 max-w-xs">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/20" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles..." className="w-full bg-navy border border-gold/[0.06] rounded-lg pl-9 pr-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:border-gold/20" data-testid="search-articles" />
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-cream/20 text-sm">Loading...</div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={32} className="mx-auto text-cream/10 mb-3" />
            <p className="text-cream/30 text-sm">No articles yet</p>
            <Link to="/admin/articles/new" className="text-gold text-sm mt-2 inline-block hover:text-gold-light">Create your first article</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gold/[0.04]">
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 font-medium">Title</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 font-medium hidden md:table-cell">Author</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 font-medium hidden sm:table-cell">Publish Date</th>
                <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => {
                const StatusIcon = statusIcons[a.status] || FileText;
                const publishDate = a.publish_at ? new Date(a.publish_at) : null;
                const isFuture = publishDate && publishDate > new Date();
                return (
                  <tr key={a.id} className="border-b border-gold/[0.03] hover:bg-cream/[0.01] transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/admin/articles/${a.id}/edit`} className="text-cream/70 hover:text-gold transition-colors font-medium">{a.title}</Link>
                      {a.pinned && <span className="ml-2 text-[9px] bg-gold/10 text-gold/60 px-1.5 py-0.5 rounded">Pinned</span>}
                      {isFuture && a.status === 'published' && <span className="ml-2 text-[9px] bg-amber-500/10 text-amber-300 px-1.5 py-0.5 rounded">Scheduled</span>}
                    </td>
                    <td className="px-4 py-3 text-cream/30 hidden md:table-cell">{a.author}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${statusColors[a.status] || ''}`}>
                        <StatusIcon size={10} /> {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs hidden sm:table-cell">
                      {publishDate ? (
                        <span className={isFuture ? 'text-amber-300' : 'text-cream/20'}>
                          {publishDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {isFuture && <Clock size={10} className="inline ml-1 text-amber-300/60" />}
                        </span>
                      ) : (
                        <span className="text-cream/10">No date</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {a.status === 'published' && !isFuture && <Link to={`/articles/${a.slug}`} target="_blank" className="p-1.5 text-cream/20 hover:text-gold transition-colors rounded"><Eye size={14} /></Link>}
                        <Link to={`/admin/articles/${a.id}/edit`} className="p-1.5 text-cream/20 hover:text-gold transition-colors rounded"><Edit size={14} /></Link>
                        <button onClick={() => handleDelete(a.id)} className="p-1.5 text-cream/20 hover:text-red-400 transition-colors rounded"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-cream/20 text-xs">Showing {Math.min((page - 1) * 50 + 1, total)}–{Math.min(page * 50, total)} of {total}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-xs text-cream/40 border border-gold/[0.06] rounded-lg disabled:opacity-30 hover:border-gold/20 transition-all">Previous</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="px-3 py-1.5 text-xs text-cream/40 border border-gold/[0.06] rounded-lg disabled:opacity-30 hover:border-gold/20 transition-all">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArticles;
